// React hook wrapping startDictation() with the lifecycle pieces every
// caller wants: session ref management, listening + error state, mic-tap
// haptics, sanitized error strings, and unmount cleanup.
//
// Direct startDictation() use is still fine — this hook only exists to
// stop the AI Coach composer and the Calories Quick Log from re-implementing
// the same plumbing twice (which they did pre-2026-05-23 and then drifted
// apart on the polish layer).
import { useEffect, useRef, useState, useCallback } from "react";
import { startDictation, sanitizeError } from "./dictate";
import { hapticImpact } from "./haptics";

export function useDictation({ onPartial, onFinal, onError, onEnd, lang } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  // Audio level (0…1) emitted at ~20Hz while listening. Always-on so callers
  // can render a meter without opting in; harmless 0 on the web fallback.
  const [level, setLevel] = useState(0);
  const sessionRef = useRef(null);

  const stop = useCallback(async () => {
    try { hapticImpact("light"); } catch {}
    const s = sessionRef.current;
    sessionRef.current = null;
    if (s) { try { await s.stop?.(); } catch {} }
    setListening(false);
  }, []);

  const start = useCallback(async () => {
    try { hapticImpact("light"); } catch {}
    setError(null);
    if (sessionRef.current) {
      try { await sessionRef.current.stop?.(); } catch {}
      sessionRef.current = null;
    }
    const session = await startDictation({
      onPartial,
      onFinal,
      onError: (raw) => {
        const friendly = sanitizeError(raw);
        setError(friendly);
        setListening(false);
        setLevel(0);
        onError?.(friendly, raw);
      },
      onEnd: (reason) => {
        setListening(false);
        setLevel(0);
        onEnd?.(reason);
      },
      onAudioLevel: (lvl) => setLevel(lvl),
      lang,
    });
    sessionRef.current = session;
    setListening(true);
  }, [onPartial, onFinal, onError, onEnd, lang]);

  const toggle = useCallback(async () => {
    if (listening) return stop();
    return start();
  }, [listening, start, stop]);

  // If the component unmounts mid-session (e.g. tab switch), tear down so the
  // recognizer doesn't keep running in the background.
  useEffect(() => () => {
    const s = sessionRef.current;
    sessionRef.current = null;
    if (s) { try { s.stop?.(); } catch {} }
  }, []);

  return { listening, level, error, start, stop, toggle, clearError: () => setError(null) };
}
